/**
 * Extracts modified pricing data from Redux packageDetailsMap and the worksheet's
 * local adjustment state, producing ProposalModificationItem[] payloads per package.
 *
 * Composite key format mirrors worksheetDataTransformerBeta.js:
 *   productId key = `${packageId}-${tierId}-${groupId}-${productId}`
 *   planId key    = `${packageId}-${tierId}-${planIdentifier}-${productId}`
 *
 * @param {Object} packageDetailsMap  Redux state.PriceSimulationReducer.packageDetailsMap
 * @param {string[]} selectedPackageIds  Package IDs to process
 * @param {Object} adjustments  Worksheet adjustment state: { [compositeProductId]: { [compositePlanId]: { percentage, newPrice } } }
 * @returns {Array<{ packageId: string, versionId: string, modifications: Object[] }>}
 */
export function extractDraftModifications(packageDetailsMap, selectedPackageIds, adjustments) {
    const results = [];

    for (const packageId of selectedPackageIds) {
        const pkg = packageDetailsMap[packageId];
        if (!pkg?.tiers) continue;

        const modifications = [];

        for (const tier of pkg.tiers) {
            const tierId = tier.tierId || tier.id;

            for (const group of tier.productGroups || []) {
                const groupId = group.groupId || group.id;

                for (const product of group.products || []) {
                    const productId = product.productId || product.id;
                    const compositeProductId = `${packageId}-${tierId}-${groupId}-${productId}`;

                    for (const [planIdentifier, pricePoint] of Object.entries(product.prices || {})) {
                        const compositePlanId = `${packageId}-${tierId}-${planIdentifier}-${productId}`;
                        const adj = adjustments[compositeProductId]?.[compositePlanId];
                        const adjustmentPct = adj?.percentage ?? 0;
                        const adjustedNewPrice = adj?.newPrice ?? null;
                        const currentPrice = pricePoint.currentPrice ?? 0;

                        // Only include plans the user actively adjusted in this session
                        if (!adj || (adjustmentPct === 0 && (adjustedNewPrice == null || adjustedNewPrice === currentPrice))) continue;

                        // Skip items already in a non-Draft status (PendingReview, Approved, Rejected)
                        if (pricePoint.approvalStatus != null && pricePoint.approvalStatus !== 0) continue;

                        const isTiered = (pricePoint.pricingScheme ?? 0) === 1;

                        modifications.push({
                            TierId: tierId,
                            ProductId: productId,
                            PlanIdentifier: planIdentifier,
                            PricingScheme: pricePoint.pricingScheme ?? 0,
                            AdjustmentPercentage: adjustmentPct,
                            NewPrice: isTiered ? null : (adjustedNewPrice ?? currentPrice),
                            BasePriceAtCreation: currentPrice,
                            TieredPrices: isTiered ? (pricePoint.tieredPrices ?? []) : [],
                            Status: 0,
                        });
                    }
                }
            }
        }

        if (modifications.length > 0) {
            results.push({
                packageId,
                versionId: pkg.versionId,
                modifications,
            });
        }
    }

    return results;
}

export default extractDraftModifications;
